import { motion } from "framer-motion";
import {
  Smartphone,
  Battery,
  Droplets,
  Wrench,
  Cpu,
  Shield,
  ArrowUpRight,
} from "lucide-react";
import { waLink } from "@/lib/siteConfig";

const SERVICES = [
  {
    id: "screen",
    title: "LCD Replacement",
    desc: "LCD pecah atau bergaris? Konsultasikan LCD kamu di sini! Tersedia untuk iPhone, iPad, dan MacBook berbagai seri",
    icon: Smartphone,
    span: "md:col-span-7 md:row-span-2",
    variant: "image",
    image:
      "https://images.unsplash.com/photo-1658212676082-312f25b52b3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxzbWFydHBob25lJTIwcmVwYWlyJTIwdGVjaG5pY2lhbnxlbnwwfHx8fDE3Nzg2NzQ1NDh8MA&ixlib=rb-4.1.0&q=85",
    price: "for iPhone, iPad, MacBook, Airpods",
  },
  {
    id: "battery",
    title: "Battery Service",
    desc: "Baterai kamu boros? Sering overheat? Atau battery health menurun? Konsultasikan keluhan kamu sekarang!",
    icon: Battery,
    span: "md:col-span-5",
    variant: "red",
    price: "for iPhone, iPad, MacBook, Airpods",
  },
  {
    id: "water",
    title: "Water Damage",
    desc: "Oops, device terendam air? Jangan khawatir! Segera lakukan pengecekan sebelum kerusakan semakin parah!",
    icon: Droplets,
    span: "md:col-span-3",
    variant: "yellow",
    price: "for iPhone, iPad, MacBook, Airpods",
  },
  {
    id: "diag",
    title: "Software & Diagnostics",
    desc: "Menangani berbagai kendala software mulai dari error, bootloop, stuck logo, dan kendala lainnya.",
    icon: Cpu,
    span: "md:col-span-5",
    variant: "dark",
    price: "for iPhone, iPad, MacBook, Airpods",
  },
  {
    id: "board",
    title: "Board-Level Service",
    desc: "Solusi servis untuk kerusakan pada komponen mesin Apple device dengan proses pengecekan dan pengerjaan yang presisi oleh teknisi berpengalaman.",
    icon: Wrench,
    span: "md:col-span-4",
    variant: "orange",
    price: "for iPhone, iPad, MacBook, Airpods",
  },
  {
    id: "android",
    title: "Android Service",
    desc: "Layanan service lengkap untuk semua kerusakan Android — Mulai dari LCD, baterai, charging port, hingga kerusakan IC.",
    icon: Smartphone,
    span: "md:col-span-3",
    variant: "default",
    price: "for Samsung, Xiaomi, Oppo, Vivo, etc.",
  },
];

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ServicesBento() {
  return (
    <section
      id="services"
      className="py-20 lg:py-28 bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4">Services</div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]"
              data-testid="services-heading"
            >
              Solusi lengkap untuk repair Apple device
              <br />
              <span className="text-[#FF6B00]">Dari kerusakan ringan hingga mati total</span>
            </h2>
          </div>
          <p className="max-w-md text-[#525252] text-base leading-relaxed">
            Walk-in kapan aja, device kamu siap ditangani teknisi berpengalaman dengan hasil proses service bergaransi.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[200px] gap-4">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.id}
              href={waLink(`Hi EZ Device, saya mau book ${s.title} service`)}
              target="_blank"
              rel="noopener noreferrer"
              variants={fade}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className={`bento-card ${s.span} ${
                s.variant === "red"
                  ? "bento-card-red"
                  : s.variant === "dark"
                  ? "bento-card-dark"
                  : s.variant === "yellow"
                  ? "bento-card-yellow"
                  : s.variant === "orange"
                  ? "bento-card-orange"
                  : ""
              } p-6 lg:p-8 flex flex-col justify-between group cursor-pointer no-underline`}
              data-testid={`service-card-${s.id}`}
            >
              {s.variant === "image" ? (
                <>
                  <div className="absolute inset-0 z-0">
                    <img
                      src={s.image}
                      alt={`${s.title} — Service Apple & Android EZ Device Jakarta`}
                      className="w-full h-full object-cover opacity-90"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A]/80 via-[#0A0A0A]/30 to-transparent" />
                  </div>
                  <div className="relative z-10 flex justify-between items-start text-white">
                    <s.icon className="w-7 h-7" strokeWidth={1.6} />
                    <span className="font-mono-ez text-[11px] uppercase tracking-widest opacity-80">
                      {s.price}
                    </span>
                  </div>
                  <div className="relative z-10 text-white">
                    <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-none">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm sm:text-base max-w-md opacity-90">
                      {s.desc}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 font-mono-ez text-[11px] uppercase tracking-widest border-b border-white pb-0.5 group-hover:gap-3 transition-all">
                      Book service <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-start">
                    <s.icon className="w-7 h-7" strokeWidth={1.6} />
                    <span className="font-mono-ez text-[11px] uppercase tracking-widest opacity-80">
                      {s.price}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed opacity-90">
                      {s.desc}
                    </p>
                  </div>
                </>
              )}
            </motion.a>
          ))}
        </div>

        {/* Bottom guarantee strip */}
        <div className="mt-10 border border-[#0A0A0A] p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-8 h-8 text-[#FF3333]" strokeWidth={1.5} />
            <div>
              <div className="font-display text-xl font-bold leading-tight">
                30-Day Warranty
              </div>
              <div className="text-sm text-[#525252] font-mono-ez">
                No fix, no fee · Genuine parts
              </div>
            </div>
          </div>
          <a
            href={waLink("Hi EZ Device, saya mau Free Device Check")}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ez btn-ez-dark self-start sm:self-auto"
            data-testid="services-cta"
          >
            Free Device Check
          </a>
        </div>
      </div>
    </section>
  );
}
